import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import { ArrowRight, Newspaper } from "@phosphor-icons/react";

const BASE = "https://thetrucktrackr.com";
const PAGE_SIZE = 12;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

const cover = (p) => (p?.cover_image_path ? `${API}/files/${p.cover_image_path}` : null);

function PostCard({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all ${featured ? "md:grid md:grid-cols-2" : ""}`}
      data-testid={featured ? "blog-featured-card" : "blog-post-card"}
    >
      <div className={`relative bg-primary/10 ${featured ? "h-56 md:h-full" : "h-44"}`}>
        {cover(post) ? (
          <img
            src={cover(post)}
            alt={post.cover_image_alt || post.title}
            loading={featured ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <Newspaper weight="duotone" className="h-10 w-10 text-primary/60" />
          </div>
        )}
      </div>
      <div className={featured ? "p-6 sm:p-8 flex flex-col justify-center" : "p-5"}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold">
          <span className="text-primary">{post.category}</span>
          <span className="text-muted-foreground font-medium normal-case tracking-normal">{fmtDate(post.published_at)}</span>
        </div>
        <h2 className={`mt-2 font-display font-bold tracking-tight group-hover:text-primary transition-colors ${featured ? "text-2xl sm:text-3xl" : "text-lg"}`}>
          {post.title}
        </h2>
        {post.excerpt && (
          <p className={`mt-2 text-sm text-muted-foreground leading-relaxed ${featured ? "" : "line-clamp-3"}`}>
            {post.excerpt}
          </p>
        )}
        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          Read more <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { categorySlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog/categories").then(({ data }) => setCats(data.filter((c) => c.published_count > 0))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.get("/blog/posts", { params: { category: categorySlug || undefined, page: 1, page_size: PAGE_SIZE } })
      .then(({ data }) => { setPosts(data.posts); setTotal(data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categorySlug]);

  const loadMore = () => {
    const next = page + 1;
    api.get("/blog/posts", { params: { category: categorySlug || undefined, page: next, page_size: PAGE_SIZE } })
      .then(({ data }) => { setPosts((p) => [...p, ...data.posts]); setPage(next); })
      .catch(() => {});
  };

  const activeCat = cats.find((c) => c.slug === categorySlug);
  const [featured, ...rest] = posts;
  const canonical = categorySlug ? `${BASE}/blog/category/${categorySlug}` : `${BASE}/blog`;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 sm:py-16" data-testid="blog-page">
      <PageMeta
        title={activeCat ? `${activeCat.name} — TruckTrackr Blog` : "Food Truck Blog — Guides, Events & Local Finds | TruckTrackr"}
        description={activeCat
          ? `TruckTrackr articles about ${activeCat.name.toLowerCase()} — practical, real-world content from the team behind the live food truck map.`
          : "Guides and local resources for finding food trucks, booking them for events, and running a food truck — from the team behind TruckTrackr."}
        canonical={canonical}
        jsonLd={{ "@context": "https://schema.org", "@type": "Blog", name: "TruckTrackr Blog", url: `${BASE}/blog` }}
      />

      <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">The TruckTrackr Blog</span>
      <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl sm:text-5xl">
        {activeCat ? activeCat.name : <>Street food, <span className="text-primary">found.</span></>}
      </h1>
      <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">
        Guides for finding food trucks, booking them for events, and running one — written by people who actually do it.
      </p>

      {cats.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-category-filters">
          <Link to="/blog">
            <span className={`inline-flex h-8 items-center px-3.5 rounded-full border text-xs font-semibold transition-colors ${!categorySlug ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted"}`}>
              All
            </span>
          </Link>
          {cats.map((c) => (
            <Link key={c.slug} to={`/blog/category/${c.slug}`}>
              <span className={`inline-flex h-8 items-center px-3.5 rounded-full border text-xs font-semibold transition-colors ${categorySlug === c.slug ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted"}`}>
                {c.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-12 text-sm text-muted-foreground">Loading articles…</div>
      ) : posts.length === 0 ? (
        <div className="mt-12 rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="blog-empty">
          No articles here yet — check back soon.
        </div>
      ) : (
        <>
          <div className="mt-8">
            <PostCard post={featured} featured />
          </div>
          {rest.length > 0 && (
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p) => <PostCard key={p.slug} post={p} />)}
            </div>
          )}
          {posts.length < total && (
            <div className="mt-8 text-center">
              <Button variant="outline" className="rounded-full" onClick={loadMore} data-testid="blog-load-more">
                Load more articles
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
